import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Loader2, RotateCcw, Send } from "lucide-react";

export function ReminderEditor() {
  const { data } = useQuery({ queryKey: ["nudge-template"], queryFn: async () => (await api.get("/agent/nudge-template")).data });
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState("");

  useEffect(() => { if (data) { setMsg(data.message); setPreview(data.preview_html); } }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/agent/nudge-template", { message: msg })).data,
    onSuccess: (d) => { setPreview(d.preview_html); toast.success("Reminder wording saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const test = useMutation({
    mutationFn: async () => { await api.put("/agent/nudge-template", { message: msg }); return (await api.post("/agent/nudge-template/test")).data; },
    onSuccess: (d) => { setPreview(preview); d.email_sent ? toast.success(`Test reminder sent to ${d.to}`) : toast.error("Couldn't send the test email right now"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Card className="p-4 sm:p-6 rounded-2xl mt-6 min-w-0" data-testid="reminder-editor">
      <div className="flex items-center gap-2 mb-1"><Mail className="h-5 w-5 text-forest" /><h3 className="font-heading font-semibold text-lg">Gentle reminder email</h3></div>
      <p className="text-xs text-muted-foreground mb-4">This message is emailed automatically to clients who stall. Edit the wording, then preview exactly how it will look.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
        <div className="min-w-0">
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={7} data-testid="reminder-message-input" className="rounded-xl" />
          <div className="flex flex-wrap gap-2 mt-3">
            <Button onClick={() => save.mutate()} disabled={save.isPending || !msg.trim()} data-testid="reminder-save-btn" className="rounded-full">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & preview"}
            </Button>
            <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !msg.trim()} data-testid="reminder-test-btn" className="rounded-full gap-1">
              {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send test to me</>}
            </Button>
            {data && <Button variant="ghost" onClick={() => setMsg(data.default_message)} className="rounded-full gap-1" data-testid="reminder-reset-btn"><RotateCcw className="h-4 w-4" /> Reset to default</Button>}
          </div>
        </div>
        <div className="rounded-xl overflow-hidden border border-border bg-white min-w-0">
          <iframe title="Reminder email preview" srcDoc={preview} className="w-full h-80" data-testid="reminder-preview" />
        </div>
      </div>
    </Card>
  );
}
